import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Tooltip} from '@/components/Tooltip';
import {Typography} from '@/components/Typography';
import {useGA4WithParams} from '@/hooks/tracking';
import {useMapDataSource} from '@/map/api';
import {Flexbox} from '@/styled-components';
import {palette} from '@/styles';
import {
  GrayCenterBox,
  SectionBlock,
  SectionSubTitle,
  SectionTitle,
} from '../../StyledComponents';
import {GenderAgeBarChart} from '../Chart/GenderAgeBarChart';
import {EMPTY_ITEM_TYPES, POPULATION_AGES} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {PopulationDataProps} from '../interface';
import {addGenderAgeTotalValue} from '../utils';

/**
 * 상권 비교 거주 인구 컴포넌트
 */
export const ResidentPopulationCompare = ({
  isPending,
  error,
  data: rawdata,
}: PopulationDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () =>
      dataSource?.find(
        (source) =>
          source.key === 'population' &&
          source.data.indexOf('반경/다각형') !== -1
      ),
    [dataSource]
  );

  const data = useMemo(() => {
    return addGenderAgeTotalValue(
      rawdata?.map((i) => i.population),
      POPULATION_AGES.length
    );
  }, [rawdata]);

  return (
    <Root>
      <div>
        <Flexbox>
          <SectionTitle>거주 인구(추정)</SectionTitle>
          <Tooltip
            openOnClick
            closeEvents={{
              mouseout: false,
              blur: false,
              click: true,
            }}
            iconStyle={{marginLeft: 2}}
            content={`행정안전부의 주민등록 인구수와 마이프차의 배후세대 분포를 활용해 해당 지역에 거주하는 인구를 추정한 정보입니다.`}
          />
        </Flexbox>
        <SectionSubTitle>{`${metadata?.source || '마이프차'}∙${
          metadata?.baseDate || ''
        }∙명`}</SectionSubTitle>
      </div>
      {isPending ? (
        <GrayCenterBox>
          <LoadingBar color={palette.textSecondary} />
        </GrayCenterBox>
      ) : error ? (
        <Typography variant="medium12" color={palette.textSecondary}>
          분석 실패
        </Typography>
      ) : !data ? (
        <EmptyItem type={EMPTY_ITEM_TYPES.Population} />
      ) : (
        <GenderAreaBarChartBox items={data} />
      )}
    </Root>
  );
};

const GenderAreaBarChartBox = ({items}: {items: any[]}) => {
  const tracking = useGA4WithParams();
  const [tab, setTab] = useState<number>(0);
  const compareList = items.map((_, idx) => `비교 상권 ${idx + 1}`);

  const onTabChange = (ageIndex: number) => {
    tracking('analysis_compare_liveperson', {
      age_info: POPULATION_AGES[ageIndex],
    });
    setTab(ageIndex);
  };

  return (
    <GenderAgeBarChart
      type="거주 인구"
      data={items}
      compareList={compareList}
      ageIndex={tab}
      ages={POPULATION_AGES}
      onAgeChange={onTabChange}
    />
  );
};

const Root = styled(SectionBlock)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
